'use client';

import {
  Box,
  Avatar,
  Checkbox,
  Typography,
  SystemIcon,
} from '@wishket/design-system';
import { twMerge } from 'tailwind-merge';

import { DoubleLayerSize, TripleLayerSize } from './CollectionThumbnail.types';

interface CollectionThumbnailProps {
  count?: number;
  imgSrc: string;
  isChecked?: boolean;
  isSelectable?: boolean;
  profileImgSrc?: string;
  onEdit?: () => void;
  onImgError?: () => void;
  onChecked?: () => void;
}

interface TripleLayerCollectionThumbnailProps extends CollectionThumbnailProps {
  size: TripleLayerSize;
  thirdLayerColor?: string;
  secondLayerColor?: string;
}

interface DoubleLayerCollectionThumbnailProps extends CollectionThumbnailProps {
  size: DoubleLayerSize;
  secondLayerColor?: string;
}

export const TripleLayerCollectionThumbnail = ({
  size,
  imgSrc,
  count = 0,
  isChecked,
  isSelectable,
  profileImgSrc,
  thirdLayerColor,
  secondLayerColor,
  onEdit,
  onChecked,
  onImgError,
}: TripleLayerCollectionThumbnailProps) => {
  const isLarge = size === 'xl';

  const containerStyle = twMerge(
    'relative flex items-center justify-center',
    isLarge ? 'h-[158px] w-[263px]' : 'h-[138px] w-[232px]'
  );

  const defaultLayerStyle = `absolute top-0 transform rounded-2xl bg-w-bluegray-50 border-white ${isLarge ? 'h-[148px] border-[1.8px]' : 'h-[130px] border-[1.4px]'}`;

  const firstLayerStyle = twMerge(
    defaultLayerStyle,
    isLarge ? 'w-[260px] translate-y-[7.2px]' : 'w-[232px] translate-y-[5.6px]'
  );

  const secondLayerStyle = {
    style: { backgroundColor: secondLayerColor },
    className: twMerge(
      defaultLayerStyle,
      isLarge
        ? 'w-[248px] translate-y-[3.6px]'
        : 'w-[220px] translate-y-[2.8px]'
    ),
  };

  const thirdLayerStyle = {
    style: { backgroundColor: thirdLayerColor },
    className: twMerge(defaultLayerStyle, isLarge ? 'w-[236px]' : 'w-[208px]'),
  };

  return (
    <Box
      className={containerStyle}
      data-testid="collection_thumbnail_container"
    >
      <Box
        className={thirdLayerStyle.className}
        style={thirdLayerStyle.style}
      />
      <Box
        style={secondLayerStyle.style}
        className={secondLayerStyle.className}
      />
      <Box className={firstLayerStyle}>
        <img
          src={imgSrc}
          alt="collection_thumbnail"
          className="size-full rounded-2xl object-cover"
          onError={onImgError}
        />
      </Box>
      <Box className="absolute left-[13px] top-[21px]">
        {isSelectable ? (
          <Checkbox checked={isChecked} onChange={onChecked} />
        ) : (
          <Box className="flex items-center gap-1 rounded-lg bg-black/50 px-2 py-[3px] backdrop-blur-[2px]">
            <SystemIcon name="small_collection" className="text-w-white" />
            <Typography variant="caption1" className="font-medium text-w-white">
              {count}
            </Typography>
          </Box>
        )}
      </Box>
      {profileImgSrc !== undefined && (
        <Box className="absolute -bottom-3 right-6 flex  items-center justify-center rounded-full border-[2px] border-white bg-white">
          <Avatar imgUrl={profileImgSrc} size="md" />
        </Box>
      )}
      {onEdit && (
        <Box className="absolute bottom-[13px] right-[13px] hover:-translate-y-0.5 hover:transition-transform">
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="cursor-pointer rounded-full bg-gradient-primary-45 p-2"
          >
            <SystemIcon
              name="medium_magic"
              className="text-white"
              testId="edit_icon"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const DoubleLayerCollectionThumbnail = ({
  imgSrc,
  size,
  count = 0,
  isChecked,
  isSelectable,
  secondLayerColor,
  onEdit,
  onChecked,
  onImgError,
}: DoubleLayerCollectionThumbnailProps) => {
  const containerStyle = twMerge(
    'relative flex items-center justify-center',
    size === 'md' && 'h-[138px] w-[232px]',
    size === 'sm' && 'h-[109px] w-[184px]',
    size === 'xs' && 'h-[81px] w-[136px]'
  );

  const defaultLayerStyle = twMerge(
    'absolute top-0 transform rounded-2xl bg-w-bluegray-50 border-white',
    size === 'md' && 'h-[130px] border-[1.4px]',
    size === 'sm' && 'h-[103px] border-[1.4px]',
    size === 'xs' && 'h-[76px] border-[1.4px]'
  );

  const firstLayerStyle = twMerge(
    defaultLayerStyle,
    size === 'md' && 'w-[232px] translate-y-2',
    size === 'sm' && 'w-[184px] translate-y-1.5',
    size === 'xs' && 'w-[136px] translate-y-[5px]'
  );

  const secondLayerStyle = {
    style: { backgroundColor: secondLayerColor },
    className: twMerge(
      defaultLayerStyle,
      size === 'md' && 'w-[220px]',
      size === 'sm' && 'w-[172px]',
      size === 'xs' && 'w-[126px]'
    ),
  };

  return (
    <Box className={containerStyle}>
      <Box
        style={secondLayerStyle.style}
        className={secondLayerStyle.className}
      />
      <Box className={firstLayerStyle}>
        <img
          src={imgSrc}
          alt="collection_thumbnail"
          className="size-full rounded-2xl object-cover"
          onError={onImgError}
        />
      </Box>
      <Box
        className={twMerge(
          'absolute left-[13px] top-[19px]',
          size === 'xs' && 'left-[9px] top-[14px]'
        )}
      >
        {isSelectable ? (
          <Checkbox checked={isChecked} onChange={onChecked} />
        ) : (
          <Box className="flex items-center gap-1 rounded-lg bg-black/50 px-2 py-[3px] backdrop-blur-[2px]">
            <SystemIcon name="small_collection" className="text-w-white" />
            <Typography variant="caption1" className="font-medium text-w-white">
              {count}
            </Typography>
          </Box>
        )}
      </Box>
      {onEdit && (
        <Box className="absolute bottom-[13px] right-[13px] hover:-translate-y-0.5 hover:transition-transform">
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="cursor-pointer rounded-full bg-gradient-primary-45 p-2"
          >
            <SystemIcon
              name="medium_magic"
              className="text-white"
              testId="edit_icon"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const SingleLayerCollectionThumbnail = ({
  imgSrc,
  count = 0,
  isChecked,
  isSelectable,
  onEdit,
  onChecked,
  onImgError,
}: CollectionThumbnailProps) => {
  const containerStyle = twMerge(
    'relative flex items-center justify-center w-full h-[148px]'
  );

  return (
    <Box className={containerStyle}>
      <Box className="size-full">
        <img
          src={imgSrc}
          alt="collection_thumbnail"
          className="size-full rounded-t-3xl object-cover"
          onError={onImgError}
        />
      </Box>
      <Box className="absolute left-[13px] top-[19px]">
        {isSelectable ? (
          <Checkbox checked={isChecked} onChange={onChecked} />
        ) : (
          <Box className="flex items-center gap-1 rounded-lg bg-black/50 px-2 py-[3px] backdrop-blur-[2px]">
            <SystemIcon name="small_collection" className="text-w-white" />
            <Typography variant="caption1" className="font-medium text-w-white">
              {count}
            </Typography>
          </Box>
        )}
      </Box>
      {onEdit && (
        <Box className="absolute bottom-[13px] right-[13px] hover:-translate-y-0.5 hover:transition-transform">
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="cursor-pointer rounded-full bg-gradient-primary-45 p-2"
          >
            <SystemIcon
              name="medium_magic"
              className="text-white"
              testId="edit_icon"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
